import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ArrowDown, HelpCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

const STAGE_TOOLTIPS: Record<string, string> = {
  'Widget Loaded': 'A visitor landed on a page where your chat widget is installed.',
  'Chat Opened': 'The visitor clicked on the chat bubble to open the widget.',
  'Message Sent': 'The visitor typed and sent at least one message.',
  'Conversation Engaged': 'A real back-and-forth happened — 2 or more messages exchanged.',
  'Conversation Completed': 'The conversation reached a natural end or was marked as completed.',
  'Booking Made': 'The visitor booked a consultation through the widget.',
}

interface FunnelStage {
  name: string
  count: number
  color: string
}

interface ConversionFunnelProps {
  data: FunnelStage[]
  loading?: boolean
}

export default function ConversionFunnel({ data, loading = false }: ConversionFunnelProps) {
  const maxCount = Math.max(...data.map(d => d.count), 1)

  const getStageColor = (index: number) => {
    const colors = [
      'bg-blue-500',
      'bg-indigo-500',
      'bg-violet-500',
      'bg-purple-500',
      'bg-fuchsia-500',
      'bg-pink-500',
    ]
    return colors[index % colors.length]
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conversion Funnel</CardTitle>
          <CardDescription>Track visitor journey from visit to conversion</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-muted rounded-lg" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversion Funnel</CardTitle>
        <CardDescription>
          How visitors move through each stage — percentage shows conversion from the step above
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {data.map((stage, index) => {
            const previousCount = index > 0 ? data[index - 1].count : null
            const widthPercentage = stage.count > 0 && maxCount > 0 ? Math.max(3, (stage.count / maxCount) * 100) : 0

            // Conversion rate from the previous step (only show if it makes sense)
            let stepRate: string | null = null
            if (previousCount !== null && previousCount > 0 && stage.count <= previousCount) {
              stepRate = ((stage.count / previousCount) * 100).toFixed(0)
            }

            return (
              <div key={stage.name} className="space-y-2">
                {/* Stage Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-base">{stage.name}</span>
                    {STAGE_TOOLTIPS[stage.name] && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs text-sm">
                            {STAGE_TOOLTIPS[stage.name]}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold">{stage.count.toLocaleString()}</span>
                    {stepRate !== null && (
                      <span className={cn(
                        'text-sm font-medium',
                        parseInt(stepRate) >= 50 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                      )}>
                        {stepRate}%
                      </span>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="relative w-full h-8 bg-muted rounded-lg overflow-hidden">
                  <div
                    className={cn(
                      'h-full transition-colors duration-500 flex items-center justify-end pr-3',
                      getStageColor(index)
                    )}
                    style={{ width: `${widthPercentage}%` }}
                  >
                    {widthPercentage > 15 && (
                      <span className="text-xs font-medium text-white">
                        {stage.count.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Arrow with conversion label */}
                {index < data.length - 1 && (
                  <div className="flex items-center justify-center gap-2 py-1">
                    <ArrowDown className="h-4 w-4 text-muted-foreground/40" />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Summary */}
        {data.length >= 2 && data[0].count > 0 && (
          <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-1">
                <div className="text-sm font-medium text-muted-foreground">End-to-End Conversion</div>
                <div className="text-2xl font-bold text-blue-600">
                  {((data[data.length - 1]?.count || 0) / data[0].count * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-muted-foreground">
                  {data[data.length - 1]?.count || 0} {data[data.length - 1]?.name?.toLowerCase()} from {data[0].count} {data[0].name?.toLowerCase()}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-medium text-muted-foreground">Weakest Step</div>
                <div className="text-2xl font-bold text-amber-600">
                  {(() => {
                    let worstRate = 100
                    for (let i = 1; i < data.length; i++) {
                      const prev = data[i - 1].count
                      const curr = data[i].count
                      if (prev > 0) {
                        const rate = (curr / prev) * 100
                        if (rate < worstRate) {
                          worstRate = rate
                        }
                      }
                    }
                    return worstRate < 100 ? `${worstRate.toFixed(0)}%` : '—'
                  })()}
                </div>
                <div className="text-xs text-muted-foreground">
                  {(() => {
                    let worstRate = 100
                    let worstStage = ''
                    for (let i = 1; i < data.length; i++) {
                      const prev = data[i - 1].count
                      const curr = data[i].count
                      if (prev > 0) {
                        const rate = (curr / prev) * 100
                        if (rate < worstRate) {
                          worstRate = rate
                          worstStage = data[i].name
                        }
                      }
                    }
                    return worstStage || 'All steps performing well'
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
